import { useState } from 'react';
import { format, parse, isValid } from 'date-fns';
import { pt } from 'date-fns/locale';
import { CalendarBlank, X } from '@phosphor-icons/react';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

/**
 * PROMPT 3 — Item 5: DatePicker em formato PT-PT (dd/mm/yyyy).
 * Wrapper sobre shadcn Calendar + Popover. Recebe `value` como string ISO (YYYY-MM-DD)
 * e emite ISO via `onChange`. Apresenta no formato dd/mm/yyyy localizado em pt-PT.
 */
export default function PTDatePicker({ value, onChange, placeholder = 'dd/mm/aaaa', className = '', testid, clearable = true, disabled = false }) {
  const [open, setOpen] = useState(false);
  const selected = value ? parse(value, 'yyyy-MM-dd', new Date()) : undefined;
  const formatted = selected && isValid(selected) ? format(selected, 'dd/MM/yyyy', { locale: pt }) : '';

  const handleSelect = (d) => {
    if (!d) { onChange?.(''); setOpen(false); return; }
    onChange?.(format(d, 'yyyy-MM-dd'));
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild disabled={disabled}>
        <button
          type="button"
          className={`flex items-center gap-2 border border-brand-border rounded-xl px-3 py-2 text-xs bg-white text-brand-text hover:bg-brand-bg/40 transition-colors disabled:opacity-50 ${className}`}
          data-testid={testid}
          disabled={disabled}
        >
          <CalendarBlank size={14} className="text-brand-muted" />
          <span className={formatted ? '' : 'text-brand-placeholder'}>{formatted || placeholder}</span>
          {clearable && formatted && !disabled && (
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => { e.stopPropagation(); onChange?.(''); }}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); onChange?.(''); } }}
              className="ml-auto text-brand-muted hover:text-red-500 cursor-pointer"
              data-testid={testid ? `${testid}-clear` : undefined}
            >
              <X size={12} />
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0 bg-white border-brand-border" align="start">
        <Calendar
          mode="single"
          selected={selected}
          onSelect={handleSelect}
          locale={pt}
          weekStartsOn={1}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
}
